import type { QuotaSnapshot } from "@/types/chat";
import { getClientId } from "@/utils/clientId";

const WINDOW_MS = 24 * 60 * 60 * 1000;
const MAX_SUBMISSIONS = 10;

type StoredUsage = {
    windowStart: number;
    messages: number;
    files: number;
};

function storageKey(): string {
    return `fairlegal_local_quota_${getClientId()}`;
}

function readRaw(): StoredUsage | null {
    try {
        const raw = localStorage.getItem(storageKey());
        if (!raw) return null;
        const o = JSON.parse(raw) as StoredUsage;
        if (
            typeof o.windowStart !== "number" ||
            typeof o.messages !== "number" ||
            typeof o.files !== "number"
        ) {
            return null;
        }
        return o;
    } catch {
        return null;
    }
}

function getRolledUsage(): StoredUsage {
    const now = Date.now();
    const cur = readRaw();
    if (!cur || now - cur.windowStart >= WINDOW_MS) {
        return { windowStart: now, messages: 0, files: 0 };
    }
    return cur;
}

function writeUsage(u: StoredUsage): void {
    try {
        localStorage.setItem(storageKey(), JSON.stringify(u));
    } catch {
        // ignore
    }
}

export function getLocalQuotaSnapshot(): QuotaSnapshot {
    const u = getRolledUsage();
    const totalUsed = u.messages + u.files;
    return {
        messagesUsed: u.messages,
        filesUsed: u.files,
        totalUsed,
        totalLimit: MAX_SUBMISSIONS,
        resetAt: new Date(u.windowStart + WINDOW_MS).toISOString(),
    };
}

/** Κλήση μετά από επιτυχημένο POST στο LLM όταν δεν υπάρχει server-side quota (π.χ. μόνο :8000). */
export function bumpLocalQuota(kind: "message" | "file"): QuotaSnapshot {
    const u = getRolledUsage();
    if (kind === "message") u.messages += 1;
    else u.files += 1;
    writeUsage(u);
    return getLocalQuotaSnapshot();
}

export function isQuotaSnapshot(value: unknown): value is QuotaSnapshot {
    if (!value || typeof value !== "object") return false;
    const v = value as Record<string, unknown>;
    return (
        typeof v.messagesUsed === "number" &&
        typeof v.filesUsed === "number" &&
        typeof v.totalUsed === "number" &&
        typeof v.totalLimit === "number" &&
        typeof v.resetAt === "string"
    );
}

/** Αν το API στέλνει X-Quota-* (Node proxy + CORS Expose-Headers όταν cross-origin). */
export function parseQuotaFromResponseHeaders(res: Response): QuotaSnapshot | null {
    const mu = res.headers.get("X-Quota-Messages-Used");
    const fu = res.headers.get("X-Quota-Files-Used");
    const tu = res.headers.get("X-Quota-Total-Used");
    const tl = res.headers.get("X-Quota-Total-Limit");
    const ra = res.headers.get("X-Quota-Reset-At");
    if (mu == null || fu == null || tu == null || tl == null || ra == null) {
        return null;
    }
    const messagesUsed = Number(mu);
    const filesUsed = Number(fu);
    const totalUsed = Number(tu);
    const totalLimit = Number(tl);
    if (
        [messagesUsed, filesUsed, totalUsed, totalLimit].some((n) =>
            Number.isNaN(n),
        )
    ) {
        return null;
    }
    return {
        messagesUsed,
        filesUsed,
        totalUsed,
        totalLimit,
        resetAt: ra,
    };
}
